import { useState } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import Navbar from '../components/navbar'
import NavbarFooter from '../components/navbar/NavbarFooter'
import ChangePasswordDialog from '../components/profile/ChangePasswordDialog'
import DeleteAccountDialog from '../components/profile/DeleteAccountDialog'
import EditNameDialog from '../components/profile/EditNameDialog'
import { useAppState } from '../reduxish/appState'

export default function ProfilePage() {
    const { state } = useAppState()
    const { authUser } = state.auth!
    const points = state.userPoints

    const [editNameOpen, setEditNameOpen] = useState(false)
    const [changePwOpen, setChangePwOpen] = useState(false)
    const [deleteOpen, setDeleteOpen] = useState(false)

    if (!authUser || !authUser.uid) {
        return (
            <>
                <Navbar />
                <Container sx={{ mt: 4 }}>
                    <Typography variant="h5">You are not signed in.</Typography>
                </Container>
                <NavbarFooter />
            </>
        )
    }

    return (
        <>
            <Navbar />
            <section>
                <main>
                    <Container sx={{ mt: 4 }}>
                        <Paper elevation={2} sx={{ overflow: 'hidden', mb: 4 }}>
                            <Box sx={{ bgcolor: 'primary.light', opacity: 0.4, height: 52 }} />
                            <Box sx={{ px: 3, pb: 2.5 }}>
                                <Avatar
                                    alt={authUser.displayName}
                                    src={authUser.photoURL}
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        mt: -4.5,
                                        border: '3px solid',
                                        borderColor: 'background.paper',
                                        bgcolor: 'primary.dark',
                                    }}
                                />
                                <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>
                                    {authUser.displayName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {authUser.email}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ mt: 1.5 }}
                                    onClick={() => setEditNameOpen(true)}
                                >
                                    Edit display name
                                </Button>
                            </Box>
                        </Paper>

                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Contributions</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <StatTile label="Total points" value={points?.total} />
                            <StatTile label="This month" value={points?.monthly} />
                            <StatTile label="Reports submitted" value={points?.reportCount} />
                        </Stack>

                        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>Account</Typography>
                        <Paper elevation={2} sx={{ p: 2 }}>
                            <Stack divider={<Divider flexItem />} spacing={2}>
                                {authUser.hasPassword && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            gap: 1.5,
                                            alignItems: { sm: 'center' },
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Password</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Change the password used to sign in to your account.
                                            </Typography>
                                        </Box>
                                        <Button variant="outlined" onClick={() => setChangePwOpen(true)}>
                                            Change password
                                        </Button>
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        gap: 1.5,
                                        alignItems: { sm: 'center' },
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Delete account</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Permanently deactivate your account and remove your profile information.
                                        </Typography>
                                    </Box>
                                    <Button color="error" variant="outlined" onClick={() => setDeleteOpen(true)}>
                                        Delete account
                                    </Button>
                                </Box>
                            </Stack>
                        </Paper>
                    </Container>
                </main>
            </section>
            <NavbarFooter />

            <EditNameDialog
                open={editNameOpen}
                currentName={authUser.displayName}
                onClose={() => setEditNameOpen(false)}
            />
            <ChangePasswordDialog
                open={changePwOpen}
                onClose={() => setChangePwOpen(false)}
            />
            <DeleteAccountDialog
                open={deleteOpen}
                requiresPassword={authUser.hasPassword}
                onClose={() => setDeleteOpen(false)}
            />
        </>
    )
}

function StatTile({ label, value }: { label: string, value: number | undefined }) {
    return (
        <Paper
            elevation={2}
            sx={{ p: 2, flex: 1, minWidth: 140, borderTop: '3px solid', borderColor: 'primary.main' }}
        >
            <Typography variant="overline" color="text.secondary">{label}</Typography>
            {value === undefined
                ? <Skeleton width={60} height={40} />
                : <Typography variant="h4" sx={{ fontWeight: 700 }}>{value}</Typography>
            }
        </Paper>
    )
}
